/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, ReactNode, useEffect, useState } from 'react'
import {
  Box,
  PopperPlacementType,
  Stack,
  StackProps,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  DateOrTimeView,
  DatePicker,
  DatePickerProps,
  DateTimePicker,
  DateTimePickerProps,
  DateView,
  LocalizationProvider,
  PickersActionBarAction,
  PickersLayoutContentWrapper,
  PickersLayoutRoot,
  TimePicker,
  TimePickerProps,
  TimeStepOptions,
  TimeView,
  pickersLayoutClasses,
  usePickerLayout,
} from '@mui/x-date-pickers-pro'
import { DateOrTimeViewWithMeridiem } from '@mui/x-date-pickers/internals/models'
import {
  LONG_DATE_TIME_FORMAT,
  MEDIUM_DATE_FORMAT,
  TIME_FORMAT,
  disabledGrey,
} from '$constants'
import {
  RegisterOptions,
  UseFormSetValue,
  Controller,
  FieldError,
  useFormContext,
} from 'react-hook-form'
import { LoadedFormLabel } from '../../display'
import { useCompanySettings } from '$hooks'
import LoadedDateTimePickerPopper, {
  LoadedDateTimePickerPopperProps,
} from './LoadedDateTimePickerPopper'
import CustomDateTimePickerActionBar, {
  CustomDateTimePickerActionBarProps,
} from '../CustomDateTimePickerActionBar'
import {
  InlineInputIcon,
  InputWithInlineIcon,
} from '../CustomInputBaseComponent'

// default sizes for the input width
const Sizes = {
  small: '13rem',
  medium: '15rem',
  large: '27rem',
  // These are here to allow fallback to the layout size when the user doesn't
  // specify a size (which is actually expected to be the norm).
  time: '13rem',
  date: '15rem',
  datetime: '27rem',
}

type LoadedDateTimePickerProps = {
  name: string
  /**
   * @property {Dayjs | null} maxDateTime - Use this parameter for maxDateTime | maxDate | maxTime
   * */
  maxDateTime?: Dayjs | null
  /**
   * @property {Dayjs | null} minDateTime - Use this parameter for minDateTime | minDate | minTime
   * */
  minDateTime?: Dayjs | null
  disabled?: boolean
  label?: string
  onAccept?: (date: Dayjs | null) => void
  onChange?: (date: Dayjs | null) => void
  variant?: 'input' | 'grid'
  layout?: 'datetime' | 'date' | 'time'
  fullWidth?: boolean
  /**
   * @property {string} size - Use this to override the width of the input, where the layout is different that the width you desire.
   */
  size?: 'small' | 'medium' | 'large' | 'unset'
  placeholder?: string
  views?: DateOrTimeView[] | TimeView[] | DateView[]
  actions?: PickersActionBarAction[]
  /**
   * @property {string} format - Default format for DateTime: 'DD MMMM YYYY, hh:mm a', Date: 'DD MMM YYYY' and Time: 'hh:mm a'
   * */
  format?: string
  sx?: SxProps<Theme>
  /**
   * @property {boolean} ampm - ONLY available for time and datetime pickers.
   * */
  ampm?: boolean
  /**
   * @property {(value: Dayjs, view: TimeView) => boolean} shouldDisableTime - ONLY available for time and datetime pickers.
   * */
  shouldDisableTime?: (value: Dayjs, view: TimeView) => boolean
  removeAdornment?: boolean
  /**
   * @property {StackProps} containerProps - Use this parameter for props needed for the container
   * */
  containerProps?: StackProps
  labelPosition?: 'left' | 'above'
  timeSteps?: TimeStepOptions
  disableFuture?: boolean
  disablePast?: boolean
  labelSxProps?: SxProps<Theme>
  changeOnBlur?: boolean
  popperPlacement?: PopperPlacementType
  inlineInputIcon?: InlineInputIcon
} & RegisterOptions<any>

const LoadedDateTimePicker: FC<LoadedDateTimePickerProps> = (props) => {
  const { localePreference } = useCompanySettings()
  const { value } = props

  let result: ReactNode
  if (value !== undefined) {
    result = <SelfContainedDateTimePicker {...props} />
  } else {
    result = <FormDateTimePicker {...props} />
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={localePreference.toLocaleLowerCase()}
    >
      {result}
    </LocalizationProvider>
  )
}

export default LoadedDateTimePicker

const CustomDateTimePickerLayout: FC = (props: any) => {
  const { content, actionBar } = usePickerLayout<
    unknown,
    unknown,
    DateOrTimeViewWithMeridiem
  >(props)

  return (
    <PickersLayoutRoot
      className={pickersLayoutClasses.root}
      ownerState={props}
      sx={{
        '.MuiDivider-root': {
          display: 'none',
        },
        '.MuiList-root': {
          borderLeft: 'none !important',
        },
        '.MuiDialogActions-root': {
          paddingTop: '10px',
          paddingBottom: '0px',
        },
      }}
    >
      <PickersLayoutContentWrapper
        className={pickersLayoutClasses.contentWrapper}
      >
        {content}
        {actionBar}
      </PickersLayoutContentWrapper>
    </PickersLayoutRoot>
  )
}

const FormDateTimePicker: FC<LoadedDateTimePickerProps> = (props) => {
  const {
    name,
    maxDateTime,
    minDateTime,
    disabled = false,
    label,
    onAccept,
    onChange,
    placeholder,
    variant: type = 'input',
    layout = 'datetime',
    fullWidth = false,
    views,
    actions,
    format,
    shouldDisableTime,
    ampm,
    sx,
    removeAdornment = false,
    containerProps,
    labelPosition = 'left',
    timeSteps = { hours: 1, minutes: 1, seconds: 5 },
    disableFuture = false,
    disablePast = false,
    labelSxProps,
    changeOnBlur = false,
    ...registerOptions
  } = props
  const { control, setValue } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      rules={registerOptions}
      render={({ field: { value }, fieldState: { error } }) => {
        return (
          <InternalDateTimePicker
            {...props}
            value={value ? dayjs(value) : value}
            error={error}
            setFormValue={setValue}
          />
        )
      }}
    />
  )
}

const SelfContainedDateTimePicker: FC<LoadedDateTimePickerProps> = (props) => {
  return <InternalDateTimePicker {...props} />
}

const InternalDateTimePicker: FC<
  LoadedDateTimePickerProps & {
    error?: FieldError
    setFormValue?: UseFormSetValue<any>
  }
> = ({
  name,
  value,
  disabled = false,
  label,
  onAccept,
  onChange,
  placeholder,
  variant: type = 'input',
  layout = 'datetime',
  size,
  fullWidth = false,
  views,
  actions,
  format,
  shouldDisableTime,
  ampm,
  sx,
  removeAdornment = false,
  containerProps,
  labelPosition = 'left',
  timeSteps = { hours: 1, minutes: 1, seconds: 5 },
  disableFuture = false,
  disablePast = false,
  maxDateTime = disableFuture ? dayjs() : undefined,
  minDateTime = disablePast ? dayjs() : undefined,
  error,
  labelSxProps,
  changeOnBlur,
  popperPlacement,
  setFormValue,
  required,
  inlineInputIcon,
}) => {
  const theme = useTheme<Theme>()

  const [open, setOpen] = useState<boolean | undefined>(false)
  const [focused, setFocused] = useState<boolean>(false)
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(value)
  const [prevSelectedDate, setPrevSelectedDate] = useState<Dayjs | null>(value)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const getActions = () => {
    if (actions) return actions

    switch (layout) {
      case 'date':
      case 'datetime':
        actions = ['accept', 'cancel', 'today']
        break
      case 'time':
      default:
        actions = ['accept', 'cancel']
        break
    }

    return actions
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setOpen((previousOpen) => !previousOpen)
  }

  const canBeOpen = open && Boolean(anchorEl)
  const id = canBeOpen ? 'transition-popper' : undefined

  const getFormat = (): string => {
    let originalFormat = format
    if (!originalFormat) {
      if (layout === 'datetime') {
        originalFormat = LONG_DATE_TIME_FORMAT
      } else if (layout === 'date') {
        originalFormat = MEDIUM_DATE_FORMAT
      } else if (layout === 'time') {
        originalFormat = TIME_FORMAT
      } else {
        originalFormat = LONG_DATE_TIME_FORMAT
      }
    }

    let updatedString = originalFormat

    // Find the last 'a' in the string
    const lastIndexOfA = updatedString.lastIndexOf('a')

    // Check if 'a' is found in the string
    if (lastIndexOfA !== -1 && (layout === 'datetime' || layout === 'time')) {
      // Split the string at the last 'a' and insert a space (this is needed ONLY for datetimepickers formatting)
      updatedString =
        originalFormat.slice(0, lastIndexOfA) +
        ' ' +
        originalFormat.slice(lastIndexOfA)
    }

    return updatedString
  }
  const dateFormat = getFormat()

  const handleDocumentClick = (event: any) => {
    if (open) {
      // Check if the click occurred outside of the DateTimePicker dialog
      const dialog =
        document.querySelector('.MuiPickersPopper-root') ??
        document.querySelector('.MuiPopper-root') ??
        document.querySelector('#transition-popper')

      const calendarIcon = document.getElementById(`${name}-calendar-icon`)

      if (
        dialog &&
        !dialog.contains(event.target) &&
        calendarIcon &&
        !calendarIcon.contains(event.target)
      ) {
        setFocused(false)
        //Hack to make the parent component to re draw the component because MUI datetimepicker does not
        //have a default way to don't change the date if the user clicks outside of the picker dialog
        setFormValue &&
          prevSelectedDate &&
          setFormValue(name, prevSelectedDate?.add(1, 'millisecond'))

        setOpen(false)
        setSelectedDate(prevSelectedDate)
      }
    }

    const inputBox = document.querySelector(`#${name}`)
    if (inputBox && !inputBox.contains(event.target) && !open) {
      setFocused(false)
    }
  }

  useEffect(() => {
    setSelectedDate(value)
    setPrevSelectedDate(value)
  }, [value])

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick)
    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleAccept = (date: Dayjs | null) => {
    if (onAccept) {
      if (dayjs.isDayjs(date)) {
        onAccept(date)
      } else {
        onAccept(null)
      }
    }
    setPrevSelectedDate(date)
    setSelectedDate(date)
    handleClosePicker()
  }

  const handleDateChange = (date: Dayjs | null) => {
    if (onChange) {
      onChange(date)
    }
    setSelectedDate(date)
  }

  const handleOpenPicker = () => {
    setOpen(true)
  }

  const handleClosePicker = () => {
    setOpen(false)
  }

  const removeIcon =
    removeAdornment && !focused
      ? {
          '& .MuiInputAdornment-root': { display: 'none' },
        }
      : null

  const closeCustomPopper = () => {
    setOpen(false)
    setAnchorEl(null)
  }

  const commonPickerProps: DateTimePickerProps<Dayjs> &
    TimePickerProps<Dayjs> &
    DatePickerProps<Dayjs> = {
    name: name,
    open: open,
    onOpen: handleOpenPicker,
    onClose: handleClosePicker,
    timeSteps: timeSteps,
    value: selectedDate,
    closeOnSelect: false,
    onAccept: handleAccept,
    onChange: handleDateChange,
    disabled,
    disableFuture,
    disablePast,
    desktopModeMediaQuery: '(min-width:600px)',
    slots: {
      layout: CustomDateTimePickerLayout,
      actionBar: CustomDateTimePickerActionBar,
      popper: LoadedDateTimePickerPopper as any,
      //We need to do "as any", otherwise Typescript complains for the Popper to be using something different than PopperProps
    },
    slotProps: {
      openPickerButton: {
        id: `${name}-calendar-icon`,
        onClick: handleClick,
      },
      popper: {
        id: id,
        open: open,
        anchorEl: anchorEl,
        pickerValue: selectedDate,
        prevPickerValue: prevSelectedDate,
        layout: layout,
        maxDateTime: maxDateTime,
        minDateTime: minDateTime,
        timeSteps: timeSteps,
        popperPlacement: popperPlacement,
        required: required,
        actions: getActions(),
        handleDateChange: handleDateChange,
        onAccept: handleAccept,
        onCancel: () => {
          setSelectedDate(prevSelectedDate)
          closeCustomPopper()
        },
        onClear: () => setSelectedDate(null),
        onSetToday: (currentDate) => setSelectedDate(currentDate),
        close: () => closeCustomPopper(),
      } as LoadedDateTimePickerPopperProps,
      actionBar: {
        actions: getActions(),
        minDateTime: minDateTime,
        maxDateTime: maxDateTime,
        value: selectedDate,
        required: required,
        layout: layout,
      } as CustomDateTimePickerActionBarProps,
      textField: {
        error: error != null,
        helperText: error?.message,
        onFocus: () => setFocused(true),
        onKeyDown: (event) => {
          if (required && event.key === 'Backspace') {
            event.preventDefault()
          }
        },
        onBlur: (event) => {
          const dateString = event.target.value
          if (
            !open &&
            onAccept &&
            changeOnBlur &&
            dateString &&
            dayjs(dateString).isValid()
          ) {
            const date = dayjs(dateString)
            setSelectedDate(date)
            setPrevSelectedDate(date)
            !date.isSame(selectedDate) && onAccept(date)
          }
        },
        onChange: (data: any) => {
          if (
            !open &&
            onAccept &&
            !changeOnBlur &&
            data &&
            dayjs(data).isValid()
          ) {
            onAccept(data as Dayjs)
            setSelectedDate(data)
            setPrevSelectedDate(data)
          }
        },
        fullWidth: fullWidth,
        inputProps: {
          ...(placeholder && { placeholder: placeholder }),
        },
      },
      layout: {
        sx: {
          '.MuiDivider-root': {
            display: 'none',
          },
          '.MuiList-root': {
            borderLeft: 'none !important',
          },
        },
      },
      day: {
        sx: {
          '&.MuiPickersDay-root.Mui-disabled': {
            color: disabledGrey,
          },
        },
      },
    },
    format: dateFormat,
    sx: {
      '& .Mui-focused': {
        // we hide the input icon while focused to avoid any overflow issues
        '& .LoadedIcon-with-tooltip': {
          display: 'none',
        },
      },
      '.MuiInputBase-root': {
        ...(!fullWidth && {
          ...{ width: Sizes[size || layout] },
        }),
        height: '3.2rem',
      },

      ...(type === 'grid' && {
        '.MuiInputBase-input': {
          color: `${theme.palette.primary.main} !important`,
        },
        '.MuiOutlinedInput-notchedOutline': {
          border: 0,
        },
        '.MuiButtonBase-root': {
          paddingLeft: 0,
          borderRadius: 'unset !important',
          '&:hover': {
            backgroundColor: 'unset !important',
          },
        },

        // disable Ripple Effect
        '& .MuiTouchRipple-root': {
          display: 'none !important',
        },
        '.MuiInputAdornment-root': {
          width: 'unset !important',
        },
      }),

      ...{
        boxShadow: 'none',
        '.MuiOutlinedInput-input': {
          boxShadow: 'none',
          ...(error?.message != null && {
            borderColor: theme.palette.error.main,
          }),
        },
        '.MuiFormHelperText-root': {
          marginLeft: 0,
        },
        ...removeIcon,
      },
      ...sx,
    },
  }

  // If the user has specified a inline input icon, check if it should be shown
  // and if so, extend the TextField props appropriately so it is shown.
  if (inlineInputIcon?.show()) {
    commonPickerProps.slotProps = commonPickerProps.slotProps ?? {}
    commonPickerProps.slotProps.textField = {
      ...commonPickerProps.slotProps.textField,
      inputProps: {
        ...(inlineInputIcon && {
          inputComponent: InputWithInlineIcon,
          inputProps: {
            message: inlineInputIcon.tooltipMessage,
            Icon: inlineInputIcon.show() ? inlineInputIcon.Icon : undefined,
          },
        }),
        sx: {
          '& .MuiInputBase-input': {
            // Necessary to override the width of the input field so the icon being
            // injected sits immediately beside the content entered in the input.
            fieldSizing: 'content',
            width: 'unset',
          },
          '& .MuiInputAdornment-root': {
            // Forces the end adornment to the right hand end of the input parent
            // which is necessary because the input field above is now set to
            // 'content' which means it won't fill the parent space itself.
            marginLeft: 'auto',
          },
        },
      },
    }
  }

  const dateTimePickerProps: DateTimePickerProps<Dayjs> = {
    ...commonPickerProps,
    maxDateTime: maxDateTime ?? undefined,
    minDateTime: minDateTime ?? undefined,
    views: views ?? ['year', 'month', 'day', 'hours', 'minutes'],
    shouldDisableTime: shouldDisableTime,
    ampm: ampm,
    ampmInClock: true,
    slotProps: {
      ...commonPickerProps.slotProps,
      actionBar: {
        ...commonPickerProps.slotProps?.actionBar,
      },
    },
  }

  const timePickerProps: TimePickerProps<Dayjs> = {
    ...commonPickerProps,
    maxTime: maxDateTime ?? undefined,
    minTime: minDateTime ?? undefined,
    ampm: ampm,
    ampmInClock: true,
    shouldDisableTime: shouldDisableTime,
    slotProps: {
      ...commonPickerProps.slotProps,
      actionBar: {
        ...commonPickerProps.slotProps?.actionBar,
      },
    },
  }

  const datePickerProps: DatePickerProps<Dayjs> = {
    ...commonPickerProps,
    maxDate: maxDateTime ?? undefined,
    minDate: minDateTime ?? undefined,
    slotProps: {
      ...commonPickerProps.slotProps,
      actionBar: {
        ...commonPickerProps.slotProps?.actionBar,
      },
    },
  }

  return (
    <Stack direction="row" alignItems="end" {...containerProps}>
      <Box
        id={name}
        display={'flex'}
        flex={1}
        flexDirection={labelPosition === 'left' ? 'row' : 'column'}
        alignItems={labelPosition === 'left' ? 'center' : 'self-start'}
        sx={{
          '& .MuiInputAdornment-root': { width: '2.5rem' },
        }}
      >
        {label && (
          <LoadedFormLabel
            label={label}
            labelPosition={labelPosition}
            sx={{
              ...(error?.message &&
                labelPosition === 'left' && { marginBottom: '2rem' }),
              ...labelSxProps,
            }}
          />
        )}

        {layout === 'datetime' && <DateTimePicker {...dateTimePickerProps} />}

        {layout === 'time' && <TimePicker {...timePickerProps} />}

        {layout === 'date' && <DatePicker {...datePickerProps} />}
      </Box>
    </Stack>
  )
}
